import {
  DEFAULT_LIST_PAGE_SIZE,
  LIST_PAGE_SIZE_OPTIONS,
  type ListPageSize,
} from "@/shared/list/pagination";
import type { ApiPagedResult } from "@tm-food/api-contract";

export type ListQueryParams = {
  page: number;
  /** UI sizes 5|10|20; lookups may use up to 100. */
  pageSize: number;
  q: string;
  status: string;
  /** Domain extras (roleCode, itemType, …) — only non-empty sent to API. */
  filters: Record<string, string>;
};

export type PagedResult<T> = {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

export function mapApiPagedResult<TDto, T>(
  dto: ApiPagedResult<TDto>,
  mapItem: (row: TDto) => T,
): PagedResult<T> {
  return {
    items: dto.items.map(mapItem),
    page: dto.page,
    pageSize: dto.pageSize,
    total: dto.total,
    totalPages: dto.totalPages,
  };
}

export function defaultListQuery(
  overrides?: Partial<ListQueryParams>,
): ListQueryParams {
  return {
    page: overrides?.page ?? 1,
    pageSize: overrides?.pageSize ?? DEFAULT_LIST_PAGE_SIZE,
    q: overrides?.q ?? "",
    status: overrides?.status ?? "all",
    filters: { ...(overrides?.filters ?? {}) },
  };
}

export function isListPageSize(n: number): n is ListPageSize {
  return (LIST_PAGE_SIZE_OPTIONS as readonly number[]).includes(n);
}

export function toApiQueryParams(params: ListQueryParams): Record<string, string> {
  const out: Record<string, string> = {
    page: String(params.page),
    pageSize: String(params.pageSize),
  };
  const q = params.q.trim();
  if (q) out.q = q;
  if (params.status && params.status !== "all") out.status = params.status;
  for (const [key, value] of Object.entries(params.filters)) {
    if (value && value !== "all") out[key] = value;
  }
  return out;
}

export function buildQueryString(params: Record<string, string>): string {
  const sp = new URLSearchParams(params);
  const s = sp.toString();
  return s ? `?${s}` : "";
}
